import { useState, useEffect, useRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import domtoimage from "dom-to-image-more";

// Asset imports (Adjust image file paths/extensions as necessary for your project structure)
import certificateSmall from "../assets/images/cftn.PNG";
import certificateLarge from "../assets/images/cftp.PNG";
import generalBanner from "../assets/images/generalBanner.png";
import premiumCard from "../assets/images/pcard.png";

import {
  FaTrash,
  FaCheckCircle,
  FaFileDownload,
  FaCalendarAlt,
  FaSearch, // Added search icon
} from "react-icons/fa";

export default function DonationRecord() {
  const [selectedTab, setSelectedTab] = useState("Bkash");
  const [donationData, setDonationData] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingId, setLoadingId] = useState(null);
  const [sortLatest, setSortLatest] = useState(false); // Added state for filtering latest data
  const [searchQuery, setSearchQuery] = useState(""); // Added state for search query
  const itemsPerPage = 10;

  // Render node element tracking points
  const certificateRef = useRef(null);
  const premiumCardRef = useRef(null);
  const generalBannerRef = useRef(null);

  // Synchronized canvas target snapshot metadata cache
  const [previewData, setPreviewData] = useState({
    name: "",
    amount: 0,
    memberId: "",
  });

  useEffect(() => {
    fetch("https://mosque-backend-yfae.vercel.app/allDonation")
      .then((res) => res.json())
      .then((data) => setDonationData(data))
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  let filteredData = donationData.filter(
    (item) => item.paymentMethod === selectedTab,
  );

  // Apply search query filter if text is provided
  if (searchQuery.trim() !== "") {
    const query = searchQuery.toLowerCase();
    filteredData = filteredData.filter(
      (item) =>
        (item.name && item.name.toLowerCase().includes(query)) ||
        (item.email && item.email.toLowerCase().includes(query)) ||
        (item.phone && item.phone.includes(query)) ||
        (item.transactionId &&
          item.transactionId.toLowerCase().includes(query)),
    );
  }

  // Apply sorting filter if "Latest First" is chosen
  if (sortLatest) {
    filteredData = [...filteredData].sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0),
    );
  }

  const generateMemberId = (amount) => {
    const prefix = Number(amount) >= 100000 ? "PM" : "GM";
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `${prefix}${random}`;
  };

  const sendEmailWithPDF = async (donation) => {
    setLoadingId(donation._id || donation.transactionId);
    const generatedId = generateMemberId(donation.amount);
    const isPremium = Number(donation.amount) >= 100000;

    // Flush layout metrics values forward instantly to preview structures
    setPreviewData({
      name: donation.name,
      amount: Number(donation.amount),
      memberId: generatedId,
    });

    // Provide browser microtask pause boundary loop to finish updates safely
    await new Promise((resolve) => setTimeout(resolve, 350));

    try {
      const options = { bgcolor: "#ffffff", quality: 0.98 };
      const pdf = new jsPDF("landscape", "pt", [800, 600]);

      // Page 1: Certificate Node Capture
      const certificateUrl = await domtoimage.toPng(
        certificateRef.current,
        options,
      );
      pdf.addImage(certificateUrl, "PNG", 0, 0, 800, 600);

      if (isPremium) {
        // Page 2: Premium Member Layout Card Track
        const premiumCardUrl = await domtoimage.toPng(
          premiumCardRef.current,
          options,
        );
        pdf.addPage([800, 533]);
        pdf.addImage(premiumCardUrl, "PNG", 0, 0, 800, 533);

        // Page 3: Banner Element (Dynamically evaluated to Premium string label text)
        const generalBannerUrl = await domtoimage.toPng(
          generalBannerRef.current,
          options,
        );
        pdf.addPage([800, 533]);
        pdf.addImage(generalBannerUrl, "PNG", 0, 0, 800, 533);
      } else {
        // Page 2: Banner Element (Dynamically evaluated to General string label text)
        const generalBannerUrl = await domtoimage.toPng(
          generalBannerRef.current,
          options,
        );
        pdf.addPage([800, 533]);
        pdf.addImage(generalBannerUrl, "PNG", 0, 0, 800, 533);
      }

      const pdfBlob = pdf.output("blob");
      const formData = new FormData();
      formData.append("pdf", pdfBlob, `${donation.name}-certificate.pdf`);
      formData.append("email", donation.email);
      formData.append("name", donation.name);
      formData.append("phone", donation.phone);
      formData.append("id", donation.transactionId);

      const memberData = {
        name: donation.name,
        email: donation.email,
        phone: donation.phone,
        category: isPremium ? "Premium" : "General",
        trxId: donation.transactionId,
        memberId: generatedId,
      };

      const response = await fetch(
        "https://mosque-backend-yfae.vercel.app/sendEmail",
        {
          method: "POST",
          body: formData,
        },
      );

      if (response.ok) {
        await fetch("https://mosque-backend-yfae.vercel.app/addMember", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(memberData),
        });

        alert(`Email successfully delivered to: ${donation.email}`);
        setDonationData((prev) =>
          prev.filter((d) => d.transactionId !== donation.transactionId),
        );
      } else {
        alert("Failed to send email document.");
      }
    } catch (error) {
      console.error("PDF Component Engine Error Trace Log:", error);
      alert(
        "Error generating structural template elements. Verify asset image imports.",
      );
    } finally {
      setLoadingId(null);
    }
  };

  const downloadTableAsPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Name", "Email", "Phone", "Amount", "Transaction ID"]],
      body: filteredData.map((donation) => [
        donation.name,
        donation.email,
        donation.phone,
        donation.amount,
        donation.transactionId,
      ]),
      styles: { fillColor: [254, 243, 199] },
      headStyles: { fillColor: [249, 115, 22] },
    });
    doc.save(`donations-${selectedTab}.pdf`);
  };

  // Pagination logic calculations variables setup points
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Top Controls Card Element Area */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
        <h1 className="text-3xl font-extrabold text-center text-gray-800 tracking-tight">
          Donation Channels Verification Log
        </h1>
        <p className="text-center text-gray-500 mt-1">
          Review active records via {selectedTab} network entries
        </p>

        {/* Tab Routing Buttons */}
        <div className="flex flex-wrap justify-center gap-2 mt-6">
          {["Bkash", "Nogod", "Rocket", "Visa", "Bank"].map((method) => (
            <button
              key={method}
              className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 shadow-sm ${
                selectedTab === method
                  ? "bg-orange-500 text-white scale-105 shadow-orange-100"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => {
                setSelectedTab(method);
                setCurrentPage(1);
              }}
            >
              {method}
            </button>
          ))}
        </div>

        {/* Dynamic Latest Filter Toggle Button & Search Bar Wrapper */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4 border-t pt-4 border-gray-100">
          <button
            onClick={() => {
              setSortLatest(!sortLatest);
              setCurrentPage(1);
            }}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg border transition-all duration-200 ${
              sortLatest
                ? "bg-emerald-600 text-white border-emerald-600 shadow-sm shadow-emerald-100"
                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}
          >
            <FaCalendarAlt />
            {sortLatest ? "Showing Latest Data First" : "Filter by Latest Data"}
          </button>

          {/* Search Input Box */}
          <div className="relative w-full md:w-72">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
              <FaSearch className="w-3.5 h-3.5" />
            </span>
            <input
              type="text"
              placeholder="Search by name, email, trx ID..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Reset to first page on keystroke query filtering
              }}
              className="w-full pl-9 pr-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 placeholder-gray-400 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Primary Management Data Log Grid Wrapper */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-700 uppercase text-xs font-bold tracking-wider">
              <tr>
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Full Name</th>
                <th className="p-4 text-left">Email Address</th>
                <th className="p-4 text-left">Phone</th>
                <th className="p-4 text-center">Amount Provided</th>
                <th className="p-4 text-left">Transaction Reference</th>
                <th className="p-4 text-center">Proof Slip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {paginatedData.length > 0 ? (
                paginatedData.map((donation, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50/70 transition-colors"
                  >
                    <td className="p-4 whitespace-nowrap text-gray-500">
                      {donation.date || "N/A"}
                    </td>
                    <td className="p-4 font-semibold text-gray-900">
                      {donation.name}
                    </td>
                    <td className="p-4 text-gray-600 font-medium">
                      {donation.email}
                    </td>
                    <td className="p-4 text-gray-500 font-mono text-xs">
                      {donation.phone}
                    </td>
                    <td className="p-4 whitespace-nowrap text-center font-bold text-emerald-600">
                      ৳{Number(donation.amount).toLocaleString()}
                    </td>
                    <td className="p-4 font-mono text-xs text-gray-600 select-all">
                      {donation.transactionId}
                    </td>
                    <td className="p-4 text-center">
                      {donation.imageUrl ? (
                        <img
                          src={donation.imageUrl}
                          alt="Donation slip proof"
                          className="w-10 h-10 rounded shadow-sm object-cover mx-auto cursor-zoom-in hover:scale-105 transition-transform border"
                          onClick={() => setSelectedImage(donation.imageUrl)}
                        />
                      ) : (
                        <span className="text-gray-400 text-xs italic">
                          No Attachment
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center py-12 text-gray-400 font-medium bg-gray-50/50"
                  >
                    No active donation entries available for this payment
                    interface
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer System Navigation Control Panels Grid bar layout */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-wrap gap-4">
          <button
            onClick={downloadTableAsPDF}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs rounded-lg shadow-sm transition-all"
          >
            <FaFileDownload />
            Download Channel Log (PDF)
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-gray-300 rounded-md shadow-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs font-medium text-gray-600 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-gray-300 rounded-md shadow-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Image Popup Modal Overlay Framework element */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh] bg-white p-1.5 rounded-xl shadow-2xl">
            <img
              src={selectedImage}
              alt="Enlarged Document Verification View"
              className="max-w-full max-h-[85vh] rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* SECURE HIDDEN RENDERING BASE NODES (NO BACKGROUND URLS)    */}
      {/* ======================================================== */}
      <div
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          overflow: "hidden",
        }}
      >
        {/* Document Unit 1: Dynamic Certificate Node Layout */}
        <div
          ref={certificateRef}
          style={{
            width: "800px",
            height: "600px",
            backgroundColor: "#ffffff",
            position: "relative",
          }}
        >
          <img
            src={
              previewData.amount >= 100000 ? certificateLarge : certificateSmall
            }
            alt="Base Layout File"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "34px",
              fontWeight: "bold",
              color: "#111111",
              textAlign: "center",
              width: "550px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "'Georgia', serif",
            }}
          >
            {previewData.name}
          </div>
        </div>

        {/* Document Unit 2: Premium ID Membership Card Layout Node */}
        <div
          ref={premiumCardRef}
          style={{
            width: "800px",
            height: "533px",
            backgroundColor: "#ffffff",
            position: "relative",
            fontFamily: "'Arial', sans-serif",
          }}
        >
          <img
            src={premiumCard}
            alt="Card graphic template layout layer context"
            style={{ width: "100%", height: "100%", objectFit: "fill" }}
          />
          <div
            style={{
              position: "absolute",
              top: "320px",
              left: "440px",
              width: "265px",
              fontSize: "20px",
              fontWeight: "600",
              color: "#111111",
              textAlign: "left",
            }}
          >
            {previewData.name}
          </div>
          <div
            style={{
              position: "absolute",
              top: "379px",
              left: "440px",
              width: "240px",
              fontSize: "20px",
              fontWeight: "600",
              color: "#111111",
              textAlign: "left",
            }}
          >
            {previewData.memberId}
          </div>
          <div
            style={{
              position: "absolute",
              top: "445px",
              left: "440px",
              width: "240px",
              fontSize: "20px",
              fontWeight: "600",
              color: "#111111",
              textAlign: "left",
            }}
          >
            Premium
          </div>
        </div>

        {/* Document Unit 3: Dynamic Banner Layout Node (Handles structural text printing dynamically) */}
        <div
          ref={generalBannerRef}
          style={{
            width: "800px",
            height: "533px",
            backgroundColor: "#ffffff",
            position: "relative",
            fontFamily: "'Arial', sans-serif",
          }}
        >
          <img
            src={generalBanner}
            alt="Banner graphic template layout layer context"
            style={{ width: "100%", height: "100%", objectFit: "fill" }}
          />
          <div
            style={{
              position: "absolute",
              top: "150px",
              left: "360px",
              width: "165px",
              fontSize: "10px",
              fontWeight: "700",
              color: "#C8A03A",
              textAlign: "center",
              letterSpacing: "0.3px",
            }}
          >
            {previewData.name}
          </div>
          <div
            style={{
              position: "absolute",
              top: "180px",
              left: "370px",
              width: "120px",
              fontSize: "11px",
              fontWeight: "700",
              color: "#C8A03A",
              textAlign: "center",
            }}
          >
            {previewData.memberId}
          </div>
          <div
            style={{
              position: "absolute",
              top: "213px",
              left: "370px",
              width: "120px",
              fontSize: "11px",
              fontWeight: "700",
              color: "#C8A03A",
              textAlign: "center",
            }}
          >
            {previewData.amount >= 100000 ? "Premium" : "General"}
          </div>
        </div>
      </div>
    </div>
  );
}
